const SYSTEM_ID_STORAGE_KEY = "blufield_system_id";
const CLIENT_IP_STORAGE_KEY = "blufield_client_ip";

let identityCache = null;
let initPromise = null;

const generateSystemId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `bf-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const getOrCreateSystemId = () => {
  let systemId = localStorage.getItem(SYSTEM_ID_STORAGE_KEY);
  if (!systemId) {
    systemId = generateSystemId();
    localStorage.setItem(SYSTEM_ID_STORAGE_KEY, systemId);
  }
  return systemId;
};

const buildClientMetadata = () => ({
  platform: "web",
  userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  language: typeof navigator !== "undefined" ? navigator.language : "",
  timezone:
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "",
  screenResolution:
    typeof window !== "undefined" && window.screen
      ? `${window.screen.width}x${window.screen.height}`
      : "",
});

const fetchPublicIp = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch("https://api.ipify.org?format=json", {
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data?.ip || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

const fetchGeolocation = () =>
  new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ latitude: null, longitude: null });
      return;
    }

    const timeoutId = setTimeout(() => {
      resolve({ latitude: null, longitude: null });
    }, 10000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        clearTimeout(timeoutId);
        resolve({ latitude: null, longitude: null });
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  });

const buildIdentity = (systemId, ipAddress, latitude = null, longitude = null) => ({
  systemId,
  ipAddress: ipAddress || "",
  latitude,
  longitude,
  clientMetadata: buildClientMetadata(),
});

export const initClientIdentity = async () => {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const systemId = getOrCreateSystemId();
    let ipAddress = sessionStorage.getItem(CLIENT_IP_STORAGE_KEY);

    if (!ipAddress) {
      ipAddress = await fetchPublicIp();
      if (ipAddress) {
        sessionStorage.setItem(CLIENT_IP_STORAGE_KEY, ipAddress);
      }
    }

    identityCache = buildIdentity(systemId, ipAddress);
    return identityCache;
  })();

  return initPromise;
};

export const captureClientLocation = async () => {
  const { latitude, longitude } = await fetchGeolocation();
  const current = getClientIdentity();
  identityCache = buildIdentity(
    current.systemId,
    current.ipAddress,
    latitude,
    longitude
  );
  return { latitude, longitude };
};

export const getClientIdentity = () => {
  if (!identityCache) {
    const systemId = getOrCreateSystemId();
    const ipAddress = sessionStorage.getItem(CLIENT_IP_STORAGE_KEY) || "";
    identityCache = buildIdentity(systemId, ipAddress);
  }
  return identityCache;
};

export const getClientIdentityHeaders = () => {
  const { systemId, ipAddress } = getClientIdentity();
  const headers = {
    "X-App-Type": "WEB",
    "X-System-Id": systemId,
  };

  if (ipAddress) {
    headers["X-Client-IP"] = ipAddress;
  }

  return headers;
};

export const withClientIdentity = (payload = {}) => {
  const { systemId, ipAddress, latitude, longitude, clientMetadata } =
    getClientIdentity();

  return {
    ...payload,
    systemId,
    ...(ipAddress ? { ipAddress } : {}),
    ...(latitude != null ? { latitude } : {}),
    ...(longitude != null ? { longitude } : {}),
    clientMetadata,
  };
};
